import aiService from "./aiService";
import edgeFunctionService from "./edgeFunctionService";
import { readPartialJsonString } from "../assistant/streamBuffer";

/**
 * Model transport for Inky, the chat agent.
 *
 * Two protocols, chosen once per session from the gemini-proxy capabilities probe:
 *  - native (proxy v2 with `contents` + `tools`): real multi-turn contents with
 *    functionCall/functionResponse parts and Gemini function calling, streamed through
 *    `aiService.streamTask({ task: 'chat.agent', profile: 'chat' })`. Text streams as plain
 *    markdown and tool calls arrive as structured parts.
 *  - legacy (proxy v1, single-turn `prompt` only): the conversation is flattened into one
 *    prompt and the model answers with a one-object JSON envelope
 *    (`{"type":"message","content"}` or `{"type":"tool_call","toolName","args"}`). Only the
 *    decoded `content` string is streamed to the UI, never the raw JSON.
 *
 * The agent loop itself (tools, approvals, persistence) lives in src/assistant/agentLoop.js.
 */

/** Errors that mean "this proxy cannot do native contents/tools" (old or rolled-back proxy). */
const LEGACY_PROXY_ERROR = /prompt is required|needs the updated ai proxy/i;

/**
 * Strip markdown code fences.
 * @param {string} text - Raw text.
 * @returns {string} Unfenced text.
 */
const stripCodeFences = (text) =>
  String(text || "")
    .trim()
    .replace(/^```(?:json)?\s*\n?/i, "")
    .replace(/\n?```\s*$/i, "")
    .trim();

/**
 * Extract the first balanced `{...}` object, respecting string escapes.
 * @param {string} text - Raw text.
 * @returns {string|null} JSON substring.
 */
const extractFirstJsonObject = (text) => {
  const s = stripCodeFences(text);
  const start = s.indexOf("{");
  if (start === -1) return null;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < s.length; i += 1) {
    const c = s[i];
    if (escape) {
      escape = false;
    } else if (c === "\\" && inString) {
      escape = true;
    } else if (c === '"') {
      inString = !inString;
    } else if (!inString && c === "{") {
      depth += 1;
    } else if (!inString && c === "}") {
      depth -= 1;
      if (depth === 0) return s.slice(start, i + 1);
    }
  }
  return null;
};

/**
 * Parse the legacy JSON envelope. Unparseable output is treated as a plain message.
 * @param {string} text - Raw model output.
 * @returns {{type: 'message'|'tool_call', content?: string, toolName?: string, args?: object}} Envelope.
 */
export const parseLegacyEnvelope = (text) => {
  const raw = String(text || "").trim();
  if (!raw) return { type: "message", content: "" };
  const candidate = extractFirstJsonObject(raw);
  if (candidate) {
    try {
      const parsed = JSON.parse(candidate);
      if (parsed?.type === "tool_call" && parsed.toolName) {
        return { type: "tool_call", toolName: String(parsed.toolName), args: parsed.args && typeof parsed.args === "object" ? parsed.args : {} };
      }
      if (parsed && parsed.content != null) {
        return { type: "message", content: String(parsed.content) };
      }
    } catch {
      // Fall through to the partial reader / plain text.
    }
  }
  const partial = readPartialJsonString(raw, "content");
  if (partial) return { type: "message", content: partial };
  return { type: "message", content: stripCodeFences(raw) };
};

/**
 * Chat agent model transport.
 */
class ChatAgentService {
  constructor() {
    this.forceLegacy = false;
  }

  /**
   * Which protocol to use for a turn. The probe itself is cached per session by
   * edgeFunctionService (and reset there when the proxy is rolled back).
   * @returns {Promise<{native: boolean, proxyVersion: number}>} Capabilities.
   */
  async getCapabilities() {
    if (this.forceLegacy) return { native: false, proxyVersion: 1 };
    try {
      const caps = await edgeFunctionService.getProxyCapabilities();
      const features = Array.isArray(caps?.features) ? caps.features : [];
      const proxyVersion = Number(caps?.proxyVersion) || 1;
      return {
        native: proxyVersion >= 2 && features.includes("tools") && features.includes("contents"),
        proxyVersion,
      };
    } catch (error) {
      console.warn("[inky] proxy capability probe failed; using legacy protocol", error);
      return { native: false, proxyVersion: 1 };
    }
  }

  /**
   * One native function-calling round, streamed.
   * @param {object} params - Round inputs.
   * @param {Array<object>} params.contents - Gemini contents.
   * @param {string} params.systemInstruction - System instruction.
   * @param {Array<object>} params.functionDeclarations - Tool declarations.
   * @param {object} [params.toolConfig] - functionCallingConfig override.
   * @param {number} params.seed - Deterministic seed.
   * @param {string} [params.accountId] - Active account id.
   * @param {AbortSignal} [params.signal] - Abort signal.
   * @param {(delta: string) => void} [params.onText] - Visible text deltas.
   * @returns {Promise<{text: string, functionCalls: Array<{name: string, args: object, thoughtSignature?: string}>, modelContent: object|null, model?: string, finishReason?: string}>} Round result (`modelContent` is the model turn with thought signatures, to append verbatim).
   */
  async callNative({ contents, systemInstruction, functionDeclarations, toolConfig, seed, accountId, signal, onText }) {
    const streamedCalls = [];
    let streamedText = "";
    let streamError = null;
    const result = await aiService.streamTask({
      task: "chat.agent",
      profile: "chat",
      contents,
      systemInstruction,
      ...(functionDeclarations?.length ? { tools: [{ functionDeclarations }] } : {}),
      toolConfig: toolConfig || { functionCallingConfig: { mode: functionDeclarations?.length ? "AUTO" : "NONE" } },
      seed,
      signal,
      ...(accountId ? { accountId } : {}),
      onEvent: (event) => {
        if (!event || typeof event !== "object") return;
        if (event.type === "text" && typeof event.text === "string") {
          streamedText += event.text;
          onText?.(event.text);
        } else if (event.type === "functionCall" && event.name) {
          streamedCalls.push({
            name: event.name,
            args: event.args || {},
            ...(event.thoughtSignature || event.thought_signature
              ? { thoughtSignature: event.thoughtSignature || event.thought_signature }
              : {}),
          });
        } else if (event.type === "error") {
          streamError = new Error(event.message || "The AI service returned an error.");
          if (event.code) streamError.code = event.code;
        }
      },
    });
    if (streamError) throw streamError;
    const resultCalls = Array.isArray(result?.functionCalls) ? result.functionCalls : [];
    const functionCalls = (resultCalls.length ? resultCalls : streamedCalls).map((call, index) => ({
      name: call.name,
      args: call.args || {},
      thoughtSignature: call.thoughtSignature || call.thought_signature || streamedCalls[index]?.thoughtSignature,
    }));
    return {
      text: typeof result?.text === "string" && result.text ? result.text : streamedText,
      functionCalls,
      modelContent: result?.modelContent && Array.isArray(result.modelContent.parts) ? result.modelContent : null,
      model: result?.model,
      finishReason: result?.finishReason,
    };
  }

  /**
   * One legacy round: flattened prompt, JSON envelope reply. Only the envelope's decoded
   * `content` is forwarded to `onText`.
   * @param {object} params - Round inputs.
   * @param {string} params.prompt - Flattened prompt.
   * @param {string} params.systemInstruction - System instruction (includes the reply format).
   * @param {number} params.seed - Deterministic seed.
   * @param {string} [params.accountId] - Active account id.
   * @param {AbortSignal} [params.signal] - Abort signal.
   * @param {(delta: string) => void} [params.onText] - Visible text deltas.
   * @returns {Promise<{type: string, content?: string, toolName?: string, args?: object}>} Parsed envelope.
   */
  async callLegacy({ prompt, systemInstruction, seed, accountId, signal, onText }) {
    let buffer = "";
    let emitted = "";
    const forward = (delta) => {
      buffer += delta;
      if (/"type"\s*:\s*"tool_call"/.test(buffer)) return;
      const visible = readPartialJsonString(buffer, "content");
      if (visible && visible.length > emitted.length && visible.startsWith(emitted)) {
        onText?.(visible.slice(emitted.length));
        emitted = visible;
      }
    };

    let streamError = null;
    const result = await aiService.streamTask({
      task: "chat.agent",
      profile: "chat",
      prompt,
      systemInstruction,
      seed,
      signal,
      ...(accountId ? { accountId } : {}),
      onEvent: (event) => {
        if (event?.type === "text" && typeof event.text === "string") forward(event.text);
        if (event?.type === "error") streamError = new Error(event.message || "The AI service returned an error.");
      },
    });
    if (streamError) throw streamError;
    const fullText = typeof result?.text === "string" && result.text ? result.text : buffer;
    return parseLegacyEnvelope(fullText || buffer);
  }

  /**
   * Remember that the proxy rejected native contents (old proxy despite the probe).
   * @param {Error} error - Error from a native round.
   * @returns {boolean} True when the session switched to the legacy protocol.
   */
  degradeOnLegacyError(error) {
    const legacy = error?.code === "TOOLS_UNSUPPORTED" || LEGACY_PROXY_ERROR.test(String(error?.message || ""));
    if (!legacy) return false;
    this.forceLegacy = true;
    return true;
  }

  /**
   * Short conversation title from the first exchange (fast utility task). Best effort.
   * @param {{userText: string, replyText: string, seed: number}} params - Title inputs.
   * @returns {Promise<string|null>} Title, or null.
   */
  async suggestTitle({ userText, replyText, seed }) {
    if (typeof aiService.runTask !== "function") return null;
    try {
      const result = await aiService.runTask({
        task: "chat.title",
        profile: "extract",
        seed,
        systemInstruction: "Write a 2-6 word title for this chat. Plain words, no quotes, no trailing punctuation.",
        prompt: `User: ${String(userText || "").slice(0, 600)}\nAssistant: ${String(replyText || "").slice(0, 600)}`,
        responseSchema: { type: "object", properties: { title: { type: "string" } }, required: ["title"] },
      });
      const title = String(result?.json?.title || parseLegacyTitle(result?.text) || "").replace(/["'\n]/g, "").trim();
      return title ? title.slice(0, 80) : null;
    } catch {
      return null;
    }
  }
}

/**
 * Read a title from a non-JSON task response.
 * @param {string} text - Raw text.
 * @returns {string} Title text.
 */
const parseLegacyTitle = (text) => {
  const json = extractFirstJsonObject(text || "");
  if (json) {
    try {
      return JSON.parse(json).title || "";
    } catch {
      return "";
    }
  }
  return String(text || "").split("\n")[0];
};

/**
 * Singleton chat agent transport.
 */
const chatAgentService = new ChatAgentService();
export default chatAgentService;
